import { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { getApiMessage, isHitLabClientEnabled } from './hitLabUtils';
import './hitLab.css';

const STATUS_OPTIONS = [
  'ready_for_curator_listen',
  'rejected',
  'hold',
  'approved_members_rotation',
  'hitlab_candidate',
  'street_drop_candidate',
];

const getPlayerRecord = (player) => player?.data?.player || player?.player || player || null;

export default function HitLabAdmin() {
  const { player } = useContext(AuthContext);
  const record = getPlayerRecord(player);
  const isAdmin = String(record?.role || '').trim() === 'admin';
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const clientEnabled = isHitLabClientEnabled();

  const loadRows = () =>
    axiosConn.get('hitlab/admin/intakes').then((response) => {
      setRows(response.data?.data || []);
    });

  useEffect(() => {
    if (!clientEnabled || !isAdmin) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    loadRows()
      .catch((err) => {
        if (active) {
          setError(getApiMessage(err, 'HitLab admin queue could not be loaded.'));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clientEnabled, isAdmin]);

  const updateStatus = async (row, status) => {
    setError('');
    try {
      await axiosConn.patch(`hitlab/admin/intakes/${row.id}/status`, {
        status,
      });
      await loadRows();
    } catch (err) {
      setError(getApiMessage(err, 'Status could not be updated.'));
    }
  };

  if (!isAdmin) {
    return (
      <main className="hitlab-page hitlab-admin">
        <section className="hitlab-shell">
          <h1>HitLab admin unavailable</h1>
          <p>This queue is restricted to admins.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="hitlab-page hitlab-admin">
      <Helmet>
        <title>JG HitLab Admin Queue</title>
      </Helmet>
      <section className="hitlab-shell">
        <p className="hitlab-kicker">ADMIN QUEUE</p>
        <h1>JG HitLab Artist Intakes</h1>
        {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
        {!clientEnabled ? (
          <div className="hitlab-alert">Artist intake is disabled.</div>
        ) : loading ? (
          <div className="hitlab-alert">Loading queue...</div>
        ) : (
          <div className="hitlab-table-wrap">
            <table className="hitlab-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Artist</th>
                  <th>Song</th>
                  <th>Genre</th>
                  <th>Paid</th>
                  <th>Rights</th>
                  <th>Listen</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '--'}</td>
                    <td>
                      <strong>{row.artistName || 'Unsubmitted artist'}</strong>
                      <span>{row.cityRegion || 'No city yet'}</span>
                    </td>
                    <td>{row.songTitle || 'No song yet'}</td>
                    <td>{row.genreLane || '--'}</td>
                    <td>{row.paymentStatus === 'paid' ? 'Paid' : 'Pending'}</td>
                    <td>
                      {row.rights?.required?.privateReviewPlayback
                        ? 'Confirmed'
                        : 'Pending'}
                    </td>
                    <td>
                      {row.songPrivateUrl ? (
                        <a
                          href={row.songPrivateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </a>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td>
                      <select
                        aria-label={`Status for ${row.artistName || row.id}`}
                        value={row.status}
                        onChange={(event) => updateStatus(row, event.target.value)}
                      >
                        <option value={row.status}>{row.status}</option>
                        {STATUS_OPTIONS.filter((status) => status !== row.status).map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
                {!rows.length ? (
                  <tr>
                    <td colSpan="8">No HitLab intakes yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
